import { AsyncStatus } from 'lib-shared-types-api';
import { createContext, Dispatch, SetStateAction } from 'react';
import { noop } from 'ts-essentials';

import {
  CommandPaletteAction,
  CommandPalettePerformActionFn,
  CommandPaletteRegisterActionsFn,
  CommandPaletteScope,
} from 'utils/commandPalette';

/** Represents the value of the command palette context. */
export type CommandPaletteContextValue = {
  /** The currently available actions. */
  readonly actions: readonly CommandPaletteAction[];
  /** The scope of the query. */
  readonly scope?: CommandPaletteScope;
  /** The full search query. */
  readonly query: string;
  /** The query of the scope. */
  readonly scopeQuery: string;
  /** Whether the command palette list is active. */
  readonly isActive: boolean;
  /** Whether the command palette is open. */
  readonly isOpen: boolean;
  /**
   * The async status of an ongoing operation inside command palette, e.g.
   * fetching data, searching, etc.
   */
  readonly asyncStatus: AsyncStatus;
  /** The type ahead text. */
  readonly typeAhead: string;
  /** The function for setting the type ahead text. */
  readonly setTypeAhead: Dispatch<SetStateAction<string>>;
  /** The function for setting the async status. */
  readonly setAsyncStatus: Dispatch<SetStateAction<AsyncStatus>>;
  /** The function for setting the active state. */
  readonly setIsActive: Dispatch<SetStateAction<boolean>>;
  /** The function for setting the open state. */
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  /** The function for setting the query. */
  readonly setQuery: Dispatch<SetStateAction<string>>;
  /** The function for registering actions. */
  readonly registerActions: CommandPaletteRegisterActionsFn;
  /** The callback for performing an action. */
  readonly performAction: CommandPalettePerformActionFn;
};

/** Represents the command palette context. */
export const CommandPaletteContext = createContext<CommandPaletteContextValue>({
  actions: [],
  asyncStatus: 'fulfilled',
  isActive: false,
  isOpen: false,
  query: '',
  scopeQuery: '',
  typeAhead: '',
  performAction: noop,
  registerActions: () => noop,
  setAsyncStatus: noop,
  setIsActive: noop,
  setIsOpen: noop,
  setQuery: noop,
  setTypeAhead: noop,
});
